/**
 * PurchaseOrderDetail.js
 * ZENO ERP — Procurement Engine
 * Last updated: 31 May 2026
 *
 * TO-16 CHANGES:
 *   — approvePO(): draft → "approved" (NOT directly to "ordered")
 *   — markAsOrdered(): "approved" → "ordered" (new function)
 *   — canMarkOrdered() guard added
 *   — "Mark as Ordered" button shown when po_status === "approved" (SM/Owner only)
 *   — Post-approve modal updated — reflects "approved" state, not "ordered"
 *   — Notification: PO approved → store notified (non-blocking)
 *   — Notification: PO ordered → store notified (non-blocking)
 *   — STATUS_UI already had "approved" entry ✅
 *
 * EXISTING (unchanged):
 *   — canReceive(): GRN only when po_status === "ordered" ✅
 *   — canAmend(): blocked after ordered ✅
 *   — canApprove(): draft only ✅
 *   — Amendment flow ✅
 *   — PDF download ✅
 *   — Email to vendor ✅
 *   — grand_total = received_qty × rate ✅
 *
 * FIXES APPLIED (BUG 9 + BUG 10 — unchanged):
 *   — loadItemsMaster: dual-query approach
 *   — Dedup by normalized item name
 *   — amendment_log[] populated correctly
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { doc, getDoc, updateDoc, collection, getDocs, query, where, serverTimestamp, arrayUnion } from "firebase/firestore";
import { reassignSiteEngineers } from "../../services/directSitePurchaseService";
import { calculateLandedCost } from "../services/purchase/purchaseOrderService";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { markNotificationReadByReference } from "../../services/notificationService";

// ─── Category allowlist for Amend PO item search ──────────────────────────────
const PURCHASE_ALLOWED_CATEGORIES = ["MATERIAL", "CONSUMABLE", "ASSET"];
function isPurchaseCategory(cat) {
  if (!cat) return true;
  return PURCHASE_ALLOWED_CATEGORIES.includes(cat.toUpperCase().trim());
}

// ─── EmailJS Config ───────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY  = "YOUR_PUBLIC_KEY";

// ─── Status display map ───────────────────────────────────────────────────────
const STATUS_UI = {
  draft:          { label: "Draft",          color: "bg-gray-100 text-gray-700" },
  approved:       { label: "Approved",       color: "bg-blue-100 text-blue-700" },
  ordered:        { label: "Ordered",        color: "bg-indigo-100 text-indigo-700" },
  completed:      { label: "Completed",      color: "bg-green-100 text-green-700" },
  short_closed:   { label: "Short Closed",   color: "bg-yellow-100 text-yellow-800" },
  short:          { label: "Short",          color: "bg-yellow-100 text-yellow-800" },
  over:           { label: "Over Received",  color: "bg-orange-100 text-orange-800" },
  over_received:  { label: "Over Received",  color: "bg-orange-100 text-orange-800" },
  closed:         { label: "Closed",         color: "bg-gray-200 text-gray-600" },
  cancelled:      { label: "Cancelled",      color: "bg-red-100 text-red-700" },
};

// ─── Approval roles ───────────────────────────────────────────────────────────
const APPROVER_ROLES = ["sm", "owner", "admin", "manager"];

// ─── Guards ───────────────────────────────────────────────────────────────────

// canApprove: only draft POs, only approver roles
function canApprove(po, userRole) {
  if (!po) return false;
  if (po.po_status !== "draft") return false;
  if (!APPROVER_ROLES.includes(userRole)) return false;
  return true;
}

// ── TO-16: canMarkOrdered — approved → ordered ────────────────────────────────
// Only SM/Owner can mark as ordered. Only when po_status === "approved".
function canMarkOrdered(po, userRole) {
  if (!po) return false;
  if (po.po_status !== "approved") return false;
  if (!APPROVER_ROLES.includes(userRole)) return false;
  return true;
}

// canReceive: GRN only when po_status === "ordered"
const TERMINAL_PO_STATUSES = [
  "completed", "short_closed", "short", "over", "over_received", "closed", "cancelled",
];
const GRN_ALLOWED_STATUSES = ["ordered"];

function canReceive(po) {
  if (!po) return false;
  if (po.grn_locked === true) return false;
  if (TERMINAL_PO_STATUSES.includes(po.po_status)) return false;
  if (!GRN_ALLOWED_STATUSES.includes(po.po_status)) return false;
  return true;
}

// canAmend: amendment blocked after ordered
function canAmend(po) {
  if (!po) return false;
  if (po.grn_locked === true) return false;
  if ([
    "ordered", "completed", "short_closed", "short",
    "over", "over_received", "closed", "cancelled",
  ].includes(po.po_status)) return false;
  return true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function normalizeName(name = "") {
  return name.toLowerCase().trim().replace(/\s+/g, " ");
}

// grand_total = received_qty × rate (RULE: NOT ordered_qty)
function calcGrandTotal(items = []) {
  return items.reduce((sum, item) => {
    if (item._cancelled) return sum;
    const qty  = parseFloat(item.received_qty) || 0;
    const rate = parseFloat(item.rate) || 0;
    return sum + qty * rate;
  }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PurchaseOrderDetail() {
  const { poId }  = useParams();
  const navigate  = useNavigate();
  const { currentUser } = useAuth();
  const { company }     = useCompany();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [po,      setPo]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Amend PO state ──────────────────────────────────────────────────────────
  const [showAmend,   setShowAmend]   = useState(false);
  const [amendItems,  setAmendItems]  = useState([]);
  const [amendReason, setAmendReason] = useState("");
  const [amendSaving, setAmendSaving] = useState(false);
  const [amendError,  setAmendError]  = useState(null);

  // ── Approval state ──────────────────────────────────────────────────────────
  const [approving,    setApproving]    = useState(false);
  const [approveError, setApproveError] = useState(null);

  // ── Site Engineer reassignment state (Direct Site Purchase) ────────────────
  const [projectSiteEngineers, setProjectSiteEngineers] = useState([]);
  const [reassignSelection,    setReassignSelection]    = useState([]);
  const [reassigning,          setReassigning]          = useState(false);
  const [reassignError,        setReassignError]        = useState(null);

  // ── TO-16: Mark as Ordered state ────────────────────────────────────────────
  const [markingOrdered,    setMarkingOrdered]    = useState(false);
  const [markOrderedError,  setMarkOrderedError]  = useState(null);

  // ── Post-approve action modal ────────────────────────────────────────────────
  const [showPostApprove,   setShowPostApprove]   = useState(false);
  const [postApproveVendor, setPostApproveVendor] = useState(null);
  const [emailSending,      setEmailSending]      = useState(false);
  const [emailStatus,       setEmailStatus]       = useState(null);
  const [approvedPOData,    setApprovedPOData]    = useState(null);

  // ── Items master (for Amend) ─────────────────────────────────────────────────
  const [itemsMaster,   setItemsMaster]   = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const itemsMasterRef  = useRef([]);

  // ── Per-row search state ─────────────────────────────────────────────────────
  const [searchState, setSearchState] = useState({});
  const searchRefs  = useRef({});
  const reasonRef   = useRef(null);

  // ── Load PO ──────────────────────────────────────────────────────────────────
  const loadPO = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const snap = await getDoc(doc(db, "purchase_orders", poId));
      if (!snap.exists()) { setError("Purchase order not found."); return; }
      const data = { id: snap.id, ...snap.data() };
      if (data.company_id !== currentUser.company_id) { setError("Access denied."); return; }
      setPo(data);
    } catch (e) {
      console.error("[PurchaseOrderDetail] loadPO error:", e);
      setError("Failed to load purchase order.");
    } finally {
      setLoading(false);
    }
  }, [poId, currentUser.company_id]);

  // Silent reload — no loading screen, no redirect
  const silentReloadPO = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, "purchase_orders", poId));
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      if (data.company_id !== currentUser.company_id) return;
      setPo(data);
    } catch (e) {
      console.error("[silentReloadPO] error:", e);
    }
  }, [poId, currentUser.company_id]);

  useEffect(() => { loadPO(); }, [loadPO]);

  // Load the project's assigned Site Engineers once the PO (with its
  // project_id) is known — powers the reassignment picker below. Depends
  // on assigned_site_engineer_ids' *content* (joined string), not the
  // array reference itself — every silentReloadPO() creates a new array
  // even when the value hasn't changed, which would otherwise re-run this
  // effect (and re-sync reassignSelection) on every unrelated reload.
  const assignedIdsKey = (po?.assigned_site_engineer_ids || []).join(",");

  useEffect(() => {
    const loadEngineers = async () => {
      if (po?.purchase_mode !== "direct_site_purchase" || !po?.project_id) {
        setProjectSiteEngineers([]);
        return;
      }
      try {
        const projSnap = await getDoc(doc(db, "projects", po.project_id));
        const ids = projSnap.exists() ? (projSnap.data().site_engineer_ids || []) : [];
        if (ids.length === 0) { setProjectSiteEngineers([]); return; }
        const snap = await getDocs(query(
          collection(db, "users"),
          where("company_id", "==", currentUser.company_id || ""),
          where("role", "==", "site_engineer")
        ));
        setProjectSiteEngineers(
          snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => ids.includes(u.id))
        );
        setReassignSelection(po.assigned_site_engineer_ids || []);
      } catch (e) {
        console.error("[PurchaseOrderDetail] loadEngineers error:", e);
      }
    };
    loadEngineers();
  }, [po?.purchase_mode, po?.project_id, assignedIdsKey, currentUser.company_id]);

  const handleReassign = useCallback(async () => {
    if (!po) return;
    try {
      setReassigning(true);
      setReassignError(null);
      await reassignSiteEngineers({
        po_id: po.id,
        company_id: currentUser.company_id,
        assigned_site_engineer_ids: reassignSelection,
        reassigned_by: currentUser.uid,
        reassigned_by_name: currentUser.name || currentUser.email || "",
      });
      await silentReloadPO();
    } catch (e) {
      console.error("[handleReassign] error:", e);
      setReassignError(e.message || "Failed to update assignment.");
    } finally {
      setReassigning(false);
    }
  }, [po, currentUser, reassignSelection, silentReloadPO]);

  // ── loadItemsMaster ───────────────────────────────────────────────────────────
  const loadItemsMaster = useCallback(async () => {
    if (!currentUser?.company_id) return;
    try {
      setMasterLoading(true);
      const snap = await getDocs(query(collection(db, "items"), where("company_id", "==", currentUser?.company_id || "")));
      const raw  = [];

      snap.forEach((d) => {
        const item = { id: d.id, ...d.data() };

        const itemName = item.item_name?.trim() || item.name?.trim() || "";
        if (!itemName) return;
        item.item_name = itemName;

        const categoryValue = item.item_category || item.category || null;
        if (!isPurchaseCategory(categoryValue)) return;
        if (!item.item_category && item.category) item.item_category = item.category;

        raw.push(item);
      });

      // Dedup by normalized name — prefer company_id tagged
      const nameMap = new Map();
      for (const item of raw) {
        const key = normalizeName(item.item_name);
        if (!nameMap.has(key)) {
          nameMap.set(key, item);
        } else {
          const existing = nameMap.get(key);
          if (!existing.company_id && item.company_id === currentUser.company_id) {
            nameMap.set(key, item);
          }
        }
      }

      const deduped = Array.from(nameMap.values()).sort((a, b) =>
        a.item_name.localeCompare(b.item_name)
      );

      itemsMasterRef.current = deduped;
      setItemsMaster(deduped);
    } catch (e) {
      console.error("[loadItemsMaster] Error:", e);
    } finally {
      setMasterLoading(false);
    }
  }, [currentUser?.company_id]);

  // ── sendVendorEmail ───────────────────────────────────────────────────────────
  const sendVendorEmail = useCallback(async (poData, vendorEmail, vendorContactPerson) => {
    if (!vendorEmail?.trim()) return { success: false, reason: "No vendor email on file" };
    if (
      EMAILJS_SERVICE_ID  === "YOUR_SERVICE_ID" ||
      EMAILJS_TEMPLATE_ID === "YOUR_TEMPLATE_ID" ||
      EMAILJS_PUBLIC_KEY  === "YOUR_PUBLIC_KEY"
    ) {
      console.warn("[sendVendorEmail] EmailJS not configured");
      return { success: false, reason: "EmailJS not configured" };
    }
    try {
      const itemsText = (poData.items || [])
        .filter(i => !i._cancelled)
        .map((item, idx) =>
          `${idx + 1}. ${item.item_name} — Qty: ${item.ordered_qty || 0} ${item.unit || ""} @ ₹${item.rate || 0}`
        )
        .join("\n");

      const grandTotal = (poData.items || [])
        .filter(i => !i._cancelled)
        .reduce((sum, i) => sum + (Number(i.ordered_qty || 0) * Number(i.rate || 0)), 0);

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email:      vendorEmail,
          to_name:       vendorContactPerson || poData.vendor_name || "Vendor",
          po_number:     poData.po_number || poData.id || "",
          company_name:  currentUser.company_name || "ZENO HVAC ERP",
          vendor_name:   poData.vendor_name || "",
          items_table:   itemsText,
          grand_total:   `₹${grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
          approved_by:   currentUser.name || currentUser.email || "Management",
          approved_date: new Date().toLocaleDateString("en-IN"),
          po_date:       poData.created_at?.toDate
            ? poData.created_at.toDate().toLocaleDateString("en-IN")
            : new Date().toLocaleDateString("en-IN"),
        },
        EMAILJS_PUBLIC_KEY
      );
      return { success: true };
    } catch (e) {
      console.error("[sendVendorEmail] Error:", e);
      return { success: false, reason: e.message };
    }
  }, [currentUser]);

  // ── TO-16: approvePO — draft → "approved" ────────────────────────────────────
  // CHANGE: saves po_status: "approved" (not "ordered")
  // Store is notified via notification (non-blocking)
  const approvePO = useCallback(async () => {
    if (!po) return;
    if (!canApprove(po, currentUser.role)) {
      setApproveError("You don't have permission to approve this PO.");
      return;
    }
    try {
      setApproving(true);
      setApproveError(null);

      // Race condition guard
      const freshSnap = await getDoc(doc(db, "purchase_orders", poId));
      const freshPO   = { id: freshSnap.id, ...freshSnap.data() };
      if (freshPO.po_status !== "draft") {
        setApproveError("PO status has changed. Please refresh.");
        return;
      }

      // ── TO-16: Save as "approved" (not "ordered") ──────────────────────────
      await updateDoc(doc(db, "purchase_orders", poId), {
        po_status:        "approved",       // ← TO-16 FIX
        approved_by:      currentUser.uid,
        approved_by_name: currentUser.name || currentUser.email || "Unknown",
        approved_at:      serverTimestamp(),
        updated_at:       serverTimestamp(),
        updated_by:       currentUser.uid,
      });

      console.log("[approvePO] PO approved → approved:", poId);
      await silentReloadPO();

      // ── Resolve po_created notification ───────────────────────────────────
      try {
        await markNotificationReadByReference(poId, "po_created", currentUser.company_id);
      } catch (e) {
        console.warn("[approvePO] resolve notification failed (non-blocking):", e);
      }

      // ── TO-16: Notify store — PO approved ────────────────────────────────
      try {
        const { createNotification } = await import("../../services/notificationService");
        await createNotification({
          type:           "po_approved",
          title:          "PO Approved",
          body:           `${freshPO.po_number || "PO"} approved by ${currentUser.name || "Manager"} — ready to order`,
          route:          `/purchase-orders/${poId}`,
          for_roles:      ["sm", "store", "owner"],
          company_id:     currentUser.company_id,
          reference_id:   poId,
          reference_type: "purchase_order",
          priority:       "medium",
        });
      } catch (notifErr) {
        console.warn("[approvePO] Notification failed (non-blocking):", notifErr);
      }

      // ── Open post-approve modal ────────────────────────────────────────────
      const approvedPO = {
        ...freshPO,
        po_status:        "approved",
        approved_by_name: currentUser.name || currentUser.email,
      };
      setApprovedPOData(approvedPO);
      setEmailStatus(null);

      if (freshPO.vendor_id) {
        try {
          const vendorSnap = await getDoc(doc(db, "vendors", freshPO.vendor_id));
          if (vendorSnap.exists()) {
            const v = vendorSnap.data();
            setPostApproveVendor({
              email:          v.email || "",
              contact_person: v.contact_person || v.name || "",
              name:           freshPO.vendor_name || v.name || "",
              phone:          v.phone || v.mobile || v.contact_no || "",
            });
          } else {
            setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
          }
        } catch {
          setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
        }
      } else {
        setPostApproveVendor({ email: "", contact_person: "", name: freshPO.vendor_name || "", phone: "" });
      }

      setShowPostApprove(true);
    } catch (e) {
      console.error("[approvePO] Error:", e);
      setApproveError("Failed to approve PO. Please try again.");
    } finally {
      setApproving(false);
    }
  }, [po, poId, currentUser, silentReloadPO]);

  // ── TO-16: markAsOrdered — "approved" → "ordered" ────────────────────────────
  // Store generates PO PDF, sends to vendor, then marks as ordered.
  // GRN button only appears after this step.
  const markAsOrdered = useCallback(async () => {
    if (!po) return;
    if (!canMarkOrdered(po, currentUser.role)) {
      setMarkOrderedError("Only SM/Owner can mark PO as ordered.");
      return;
    }
    try {
      setMarkingOrdered(true);
      setMarkOrderedError(null);

      // Race condition guard
      const freshSnap = await getDoc(doc(db, "purchase_orders", poId));
      const freshPO   = { id: freshSnap.id, ...freshSnap.data() };
      if (freshPO.po_status !== "approved") {
        setMarkOrderedError("PO status has changed. Please refresh.");
        return;
      }

      await updateDoc(doc(db, "purchase_orders", poId), {
        po_status:        "ordered",
        ordered_by:       currentUser.uid,
        ordered_by_name:  currentUser.name || currentUser.email || "Unknown",
        ordered_at:       serverTimestamp(),
        updated_at:       serverTimestamp(),
        updated_by:       currentUser.uid,
      });

      console.log("[markAsOrdered] PO marked as ordered:", poId);
      await silentReloadPO();

      // ── Resolve po_approved notification ──────────────────────────────────
      try {
        await markNotificationReadByReference(poId, "po_approved", currentUser.company_id);
      } catch (e) {
        console.warn("[markAsOrdered] resolve notification failed (non-blocking):", e);
      }

      // ── TO-16: Notify store — PO ordered, GRN can be done ─────────────────
      try {
        const { createNotification } = await import("../../services/notificationService");
        await createNotification({
          type:           "po_ordered",
          title:          "PO Sent to Vendor",
          body:           `${freshPO.po_number || "PO"} is now ordered — receive material when it arrives`,
          route:          `/purchase-orders/${poId}`,
          for_roles:      ["sm", "store", "owner"],
          company_id:     currentUser.company_id,
          reference_id:   poId,
          reference_type: "purchase_order",
          priority:       "medium",
        });
      } catch (notifErr) {
        console.warn("[markAsOrdered] Notification failed (non-blocking):", notifErr);
      }

    } catch (e) {
      console.error("[markAsOrdered] Error:", e);
      setMarkOrderedError("Failed to mark as ordered. Please try again.");
    } finally {
      setMarkingOrdered(false);
    }
  }, [po, poId, currentUser, silentReloadPO]);

  // ── downloadPO ────────────────────────────────────────────────────────────────
  const downloadPO = useCallback(() => {
    if (!po) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW  = 210;
    const margin = 15;
    const contentW = pageW - margin * 2;

    const DARK     = [30,  30,  30];
    const GRAY     = [100, 100, 100];
    const BORDER   = [200, 200, 200];

    // ── Header: company left, PO title right ──
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(margin, 38, pageW - margin, 38);

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text(company?.company_name || currentUser.company_name || "Company", margin, 18);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(company?.tagline || company?.business_type || "", margin, 25);
    if (company?.gst_number) doc.text(`GST: ${company.gst_number}`, margin, 31);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("PURCHASE ORDER", pageW - margin, 18, { align: "right" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    doc.text(`No: ${po.po_number || po.id}`, pageW - margin, 25, { align: "right" });
    const poDate = po.created_at?.toDate
      ? po.created_at.toDate().toLocaleDateString("en-IN")
      : new Date().toLocaleDateString("en-IN");
    doc.text(`Date: ${poDate}`, pageW - margin, 31, { align: "right" });

    let y = 45;

    // ── Vendor + Order Info (plain text, no color boxes) ──
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...GRAY);
    doc.text("VENDOR", margin, y);
    doc.text("ORDER INFO", pageW / 2 + 5, y);

    y += 5;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text(po.vendor_name || "—", margin, y);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
    if (po.vendor_address) { y += 5; doc.text(po.vendor_address, margin, y, { maxWidth: 80 }); }
    if (po.vendor_phone)   { y += 5; doc.text(`Tel: ${po.vendor_phone}`, margin, y); }
    if (po.vendor_gst)     { y += 5; doc.text(`GST: ${po.vendor_gst}`, margin, y); }

    const infoRows = [
      ["PO No",       po.po_number || po.id || "—"],
      ["Date",        poDate],
      ["Status",      (po.po_status || "").toUpperCase()],
      ["Approved By", po.approved_by_name || "—"],
    ];
    let iy = 50;
    infoRows.forEach(row => {
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...DARK);
      doc.text(`${row[0]}:`, pageW / 2 + 5, iy);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...GRAY);
      doc.text(String(row[1]), pageW / 2 + 30, iy);
      iy += 6;
    });

    y = Math.max(y, iy) + 8;
    doc.setDrawColor(...BORDER);
    doc.line(margin, y - 4, pageW - margin, y - 4);

    const activeItems = (po.items || []).filter(i => !i._cancelled);
    const fmt = (n) => `Rs. ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const tableRows = activeItems.map((item, idx) => [
      idx + 1,
      item.item_name + (item.is_custom_item ? " *" : ""),
      item.unit || "—",
      Number(item.ordered_qty || 0),
      fmt(item.rate),
      fmt(Number(item.ordered_qty || 0) * Number(item.rate || 0)),
    ]);

    autoTable(doc, {
      startY: y,
      head: [["#", "Item Name", "Unit", "Qty", "Rate", "Amount"]],
      body: tableRows,
      theme: "grid",
      headStyles: { fillColor: [240, 240, 240], textColor: DARK, fontSize: 8, fontStyle: "bold", halign: "center", cellPadding: 3 },
      bodyStyles: { fontSize: 8, textColor: DARK, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { cellWidth: 65 },
        2: { halign: "center", cellWidth: 18 },
        3: { halign: "right", cellWidth: 18 },
        4: { halign: "right", cellWidth: 28 },
        5: { halign: "right", cellWidth: 31 },
      },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 5;
    const orderedTotal = activeItems.reduce(
      (sum, i) => sum + Number(i.ordered_qty || 0) * Number(i.rate || 0), 0
    );
    const tX = pageW - margin - 80;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.rect(tX, y, 80, 11);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DARK);
    doc.text("TOTAL ORDER VALUE:", tX + 3, y + 7.5);
    doc.text(
      `Rs. ${orderedTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      tX + 77, y + 7.5, { align: "right" }
    );

    if (activeItems.some(i => i.is_custom_item)) {
      y += 16;
      doc.setFontSize(7);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...GRAY);
      doc.text("* Custom item — not in standard catalogue", margin, y);
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setDrawColor(...BORDER);
      doc.line(margin, 287, pageW - margin, 287);
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...GRAY);
      doc.text(
        `${company?.company_name || currentUser.company_name || "Company"} | Generated ${new Date().toLocaleString("en-IN")} | Page ${p} of ${pageCount}`,
        pageW / 2, 292, { align: "center" }
      );
    }
    doc.save(`${po.po_number || "PO"}_${(po.vendor_name || "vendor").replace(/\s+/g, "_")}.pdf`);
  }, [po, currentUser, company]);

  // ── openAmend ────────────────────────────────────────────────────────────────
  const openAmend = useCallback(() => {
    if (!po || !canAmend(po)) return;
    const items = (po.items || []).map((item, idx) => ({
      ...item,
      _rowKey:      item.item_id || `existing_${idx}`,
      _isNew:       false,
      _cancelled:   item._cancelled || false,
      _wasReceived: (parseFloat(item.received_qty) || 0) > 0,
    }));
    setAmendItems(items);
    setAmendReason("");
    setAmendError(null);
    setSearchState({});
    loadItemsMaster();
    setShowAmend(true);
  }, [po, loadItemsMaster]);

  // ── amendItemsRef ────────────────────────────────────────────────────────────
  const amendItemsRef = useRef([]);
  useEffect(() => { amendItemsRef.current = amendItems; }, [amendItems]);

  const getSuggestions = useCallback((queryStr, rowKey) => {
    if (!queryStr?.trim() || queryStr.trim().length < 2) return [];
    const q       = normalizeName(queryStr);
    const master  = itemsMasterRef.current;
    const current = amendItemsRef.current;
    if (master.length === 0) return [];

    const inPONames = new Set(
      current
        .filter((r) => !r._cancelled && r.item_name?.trim())
        .map((r) => normalizeName(r.item_name))
    );
    const thisRow     = current.find((r) => r._rowKey === rowKey);
    const thisRowName = thisRow ? normalizeName(thisRow.item_name || "") : "";

    return master
      .filter((item) => normalizeName(item.item_name).includes(q))
      .map((item) => {
        const itemName    = normalizeName(item.item_name);
        const alreadyInPO = inPONames.has(itemName) && itemName !== thisRowName;
        return { ...item, _alreadyInPO: alreadyInPO };
      })
      .slice(0, 10);
  }, []);

  const handleSearchChange = useCallback((rowKey, value) => {
    const suggestions = getSuggestions(value, rowKey);
    const hasQuery    = value.trim().length >= 2;
    const isCustom    = hasQuery && suggestions.length === 0;
    setAmendItems((prev) =>
      prev.map((r) =>
        r._rowKey === rowKey ? { ...r, item_name: value, item_id: null, _isCustom: isCustom } : r
      )
    );
    setSearchState((prev) => ({
      ...prev,
      [rowKey]: { query: value, suggestions, open: suggestions.length > 0 },
    }));
  }, [getSuggestions]);

  const handleSelectSuggestion = useCallback((rowKey, masterItem) => {
    if (masterItem._alreadyInPO) return;
    setAmendItems((prev) =>
      prev.map((r) =>
        r._rowKey === rowKey
          ? { ...r, item_id: masterItem.item_id || "", item_name: masterItem.item_name, item_category: masterItem.item_category, unit: masterItem.unit || r.unit || "", _isCustom: false }
          : r
      )
    );
    setSearchState((prev) => ({
      ...prev,
      [rowKey]: { query: masterItem.item_name, suggestions: [], open: false },
    }));
  }, []);

  const handleAddItem = useCallback(() => {
    const rowKey = `new_${Date.now()}`;
    setAmendItems((prev) => [
      ...prev,
      { _rowKey: rowKey, _isNew: true, _isCustom: false, _cancelled: false, _wasReceived: false, item_id: null, item_name: "", item_category: "", unit: "", ordered_qty: 0, received_qty: 0, rate: 0 },
    ]);
    setSearchState((prev) => ({ ...prev, [rowKey]: { query: "", suggestions: [], open: false } }));
  }, []);

  const handleCancelItem  = useCallback((rowKey) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, _cancelled: true  } : r));
  }, []);

  const handleRestoreItem = useCallback((rowKey) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, _cancelled: false } : r));
  }, []);

  const handleRowChange = useCallback((rowKey, field, value) => {
    setAmendItems((prev) => prev.map((r) => r._rowKey === rowKey ? { ...r, [field]: value } : r));
  }, []);

  // ── handleSaveAmend ───────────────────────────────────────────────────────────
  const handleSaveAmend = useCallback(async () => {
    if (!po) return;

    const freshSnap = await getDoc(doc(db, "purchase_orders", poId));
    const freshPO   = { id: freshSnap.id, ...freshSnap.data() };
    if (!canAmend(freshPO)) {
      setAmendError("This PO has been locked and can no longer be amended.");
      return;
    }
    if (!amendReason.trim()) {
      setAmendError("Amendment reason is mandatory — please fill in the reason below.");
      setTimeout(() => {
        reasonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        reasonRef.current?.focus();
      }, 50);
      return;
    }
    const activeItems = amendItems.filter((r) => !r._cancelled);
    if (activeItems.length === 0) { setAmendError("At least one active item is required."); return; }
    for (const item of activeItems) {
      if (!item.item_name?.trim()) { setAmendError("All active items must have a name."); return; }
      if (!item.rate || parseFloat(item.rate) <= 0) { setAmendError(`Rate must be > 0 for "${item.item_name}".`); return; }
      if (!item.ordered_qty || parseFloat(item.ordered_qty) <= 0) { setAmendError(`Ordered qty must be > 0 for "${item.item_name}".`); return; }
    }

    try {
      setAmendSaving(true);
      setAmendError(null);

      // D-295: Amend keeps every field saved at PO creation (line_id,
      // base_rate, discount, GST, landed cost) — merge, never rebuild (same
      // rule as F9). Only the screen's helper keys (_rowKey, _isNew …) are
      // dropped. Landed cost is recalculated for the new qty / rate
      // (reference only — never the bill).
      const now        = Date.now();
      const builtItems = amendItems.map((item, index) => {
        const { _rowKey, _isNew, _wasReceived, _isCustom, ...saved } = item;
        const resolvedId  = item.item_id || `custom_${now}_${index}`;
        const orderedQty  = parseFloat(item.ordered_qty) || 0;
        const receivedQty = parseFloat(item.received_qty) || 0;
        const rate        = parseFloat(item.rate) || 0;
        return {
          ...saved,
          line_id:        item.line_id || `${resolvedId}_${index}`,
          item_id:        resolvedId,
          item_name:      item.item_name?.trim(),
          item_category:  item.item_category || "",
          unit:           item.unit || "",
          qty:            orderedQty,
          ordered_qty:    orderedQty,
          received_qty:   receivedQty,
          pending_qty:    Math.max(orderedQty - receivedQty, 0),
          rate,
          base_rate:      rate,
          discount_type:  item.discount_type === "flat" ? "flat" : "percent",
          discount_value: Number(item.discount_value || 0),
          gst_percent:    Number(item.gst_percent || 0),
          _cancelled:     item._cancelled || false,
          // Custom = not from Item Master (hand-typed rows get a "custom_…" id).
          is_custom_item: String(resolvedId).startsWith("custom_"),
        };
      });

      // Landed cost on ACTIVE lines only, so a cancelled line takes no freight share.
      const activeLanded = calculateLandedCost(
        builtItems.filter((i) => !i._cancelled),
        freshPO.freight_amount
      );
      let k = 0;
      const finalItems = builtItems.map((i) => (i._cancelled ? i : activeLanded[k++]));
      const landed_grand_total = activeLanded.reduce((s, i) => s + (i.landed_total || 0), 0);

      const grand_total = finalItems.reduce((sum, item) => {
        if (item._cancelled) return sum;
        return sum + (item.received_qty * item.rate);
      }, 0);

      const logEntry = {
        amended_at:        new Date().toISOString(),
        amended_by:        currentUser.uid,
        amended_by_name:   currentUser.displayName || currentUser.email || "Unknown",
        reason:            amendReason.trim(),
        items_snapshot:    finalItems,
        grand_total_after: grand_total,
      };

      await updateDoc(doc(db, "purchase_orders", poId), {
        items:           finalItems,
        grand_total,
        landed_grand_total,
        amendment_log:   arrayUnion(logEntry),
        updated_at:      serverTimestamp(),
        updated_by:      currentUser.uid,
        updated_by_name: currentUser.displayName || currentUser.email || "Unknown",
      });

      setShowAmend(false);
      await silentReloadPO();
    } catch (e) {
      console.error("[handleSaveAmend] Error:", e);
      setAmendError("Failed to save amendment. Please try again.");
    } finally {
      setAmendSaving(false);
    }
  }, [po, poId, amendItems, amendReason, currentUser, silentReloadPO]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading purchase order...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error}</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm">
          ← Back
        </button>
      </div>
    );
  }
  if (!po) return null;

  const statusUI       = STATUS_UI[po.po_status] || { label: po.po_status, color: "bg-gray-100 text-gray-600" };
  const grandTotal     = calcGrandTotal(po.items || []);
  const userRole       = currentUser.role || "";
  const amendAllowed   = canAmend(po);
  const approveAllowed = canApprove(po, userRole);
  const orderAllowed   = canMarkOrdered(po, userRole);   // TO-16
  const receiveAllowed = canReceive(po);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-1 flex items-center gap-1">
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900">{po.po_number || "Purchase Order"}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Vendor: <span className="font-medium text-gray-700">{po.vendor_name || "—"}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusUI.color}`}>
            {statusUI.label}
          </span>
          {po.grn_locked && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              GRN Locked
            </span>
          )}
        </div>
      </div>

      {/* ── PO Meta ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-gray-500">PO Date</p>
          <p className="font-medium">
            {po.created_at?.toDate
              ? po.created_at.toDate().toLocaleDateString("en-IN")
              : po.created_at
              ? new Date(po.created_at).toLocaleDateString("en-IN")
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Created By</p>
          <p className="font-medium">{po.created_by_name || "—"}</p>
        </div>
        <div>
          <p className="text-gray-500">Grand Total</p>
          <p className="font-semibold text-gray-900">
            ₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Items</p>
          <p className="font-medium">{(po.items || []).filter((i) => !i._cancelled).length}</p>
        </div>
      </div>

      {/* ── Direct Site Purchase info (Priority 4) ── */}
      {po.purchase_mode === "direct_site_purchase" && (
        <div className="mb-4 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs font-bold rounded-full">
              Direct Site Purchase
            </span>
            <span className="font-medium">{po.project_name} — {po.site_name}</span>
          </div>
          <p className="text-xs text-purple-700">
            {po.site_confirmation_status === "pending_site_confirmation" && "Awaiting Site Engineer to confirm received quantity."}
            {po.site_confirmation_status === "site_confirmed" && `Confirmed by ${po.site_confirmed_by_name || "Site Engineer"} — awaiting Manager to finalize rate.`}
            {po.site_confirmation_status === "completed" && "Finalized — site stock credited, vendor payable created."}
            {po.site_confirmation_status === "finalizing" && `Finalization started by ${po.finalizing_by_name || "a manager"} did not finish. Do not finalize again — ask admin to check site stock and the vendor bill.`}
          </p>

          {/* Reassign Site Engineer(s) — only while still awaiting
              confirmation, and only for Manager/SM/Owner. Once the
              Site Engineer has confirmed, the pin is locked (changing
              it after the fact would be meaningless). */}
          {po.site_confirmation_status === "pending_site_confirmation" &&
            ["sm", "owner", "manager"].includes(userRole) &&
            projectSiteEngineers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-purple-200">
              <p className="text-xs font-semibold text-purple-800 mb-1.5">
                Pin to specific Site Engineer(s)
              </p>
              {reassignError && <p className="text-xs text-red-600 mb-1.5">{reassignError}</p>}
              <div className="flex flex-wrap gap-3 mb-2">
                {projectSiteEngineers.map(se => {
                  const checked = reassignSelection.includes(se.id);
                  return (
                    <label key={se.id} className="flex items-center gap-1.5 text-xs text-purple-800">
                      <input type="checkbox" checked={checked}
                        onChange={e => setReassignSelection(prev =>
                          e.target.checked ? [...prev, se.id] : prev.filter(id => id !== se.id)
                        )} />
                      {se.name || se.email || se.id}
                    </label>
                  );
                })}
              </div>
              <button
                onClick={handleReassign}
                disabled={reassigning}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
              >
                {reassigning ? "Saving..." : "Update Assignment"}
              </button>
              <p className="text-xs text-purple-500 mt-1">
                Leave everyone unchecked to let anyone assigned to the project confirm it.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Approval info (if approved or ordered) ── */}
      {(po.po_status === "approved" || po.po_status === "ordered") && po.approved_by_name && (
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
          <span>✓</span>
          <span>
            Approved by <strong>{po.approved_by_name}</strong>
            {po.approved_at?.toDate &&
              ` on ${po.approved_at.toDate().toLocaleDateString("en-IN")}`}
          </span>
        </div>
      )}

      {/* ── GRN Status Badges ── */}
      {(po.po_status === "over" || po.po_status === "over_received" || po.over_received) && (
        <div className="mb-4 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 flex items-center gap-2">
          <span>⚠</span>
          <span><strong>Over Received</strong> — More material received than ordered.</span>
        </div>
      )}
      {(po.po_status === "short" || po.po_status === "short_closed") && (
        <div className="mb-4 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-center gap-2">
          <span>⚠</span>
          <span><strong>Short Received</strong> — Less material received than ordered.</span>
          {po.short_close_reason && <span className="ml-1">Reason: {po.short_close_reason}</span>}
        </div>
      )}

      {/* ── Items Table ── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-sm">Items</h2>
          {amendAllowed && (
            <button onClick={openAmend} className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 transition">
              ✏ Amend PO
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Item</th>
                <th className="px-4 py-3 text-right">Ordered</th>
                <th className="px-4 py-3 text-right">Received</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(po.items || []).map((item, idx) => {
                const receivedQty = parseFloat(item.received_qty) || 0;
                const rate        = parseFloat(item.rate) || 0;
                const amount      = receivedQty * rate;
                const isExact     = receivedQty === (parseFloat(item.ordered_qty) || 0);
                const isOver      = receivedQty > (parseFloat(item.ordered_qty) || 0);
                const rowColor    = item._cancelled ? "bg-gray-50 opacity-60" : isOver ? "bg-orange-50" : isExact && receivedQty > 0 ? "bg-green-50" : "";

                return (
                  <tr key={item.item_id || idx} className={rowColor}>
                    <td className="px-4 py-3">
                      <p className={`font-medium ${item._cancelled ? "line-through text-gray-400" : "text-gray-900"}`}>
                        {item.item_name}
                      </p>
                      <p className="text-xs text-gray-400">{item.unit || ""}</p>
                      {item.is_custom_item && <span className="text-xs text-purple-500">Custom item</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{item.ordered_qty ?? "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{receivedQty || "—"}</td>
                    <td className="px-4 py-3 text-right text-gray-700">₹{rate.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      ₹{amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item._cancelled ? (
                        <span className="text-xs text-gray-400">Cancelled</span>
                      ) : isOver ? (
                        <span className="text-xs text-orange-600 font-medium">Over</span>
                      ) : isExact && receivedQty > 0 ? (
                        <span className="text-xs text-green-600 font-medium">✓ Exact</span>
                      ) : po.grn_locked ? (
                        <span className="text-xs text-amber-600 font-medium">Short</span>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  Grand Total (received × rate)
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-900">
                  ₹{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Freight Reference (estimate vs actual) ── */}
      {(po.freight_amount != null || po.actual_freight_amount != null) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-gray-800 text-sm mb-3">Freight Reference (not part of Grand Total)</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs">Estimated at PO creation</p>
              <p className="font-medium text-gray-800">
                ₹{Number(po.freight_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Actual (confirmed at GRN)</p>
              <p className="font-medium text-gray-800">
                {po.actual_freight_amount != null
                  ? `₹${Number(po.actual_freight_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                  : <span className="text-gray-400">Not yet confirmed</span>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Amendment History ── */}
      {(po.amendment_log || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-gray-800 text-sm mb-3">Amendment History</h2>
          <div className="space-y-3">
            {(po.amendment_log || []).map((log, idx) => (
              <div key={idx} className="text-sm border-l-2 border-indigo-200 pl-3">
                <p className="font-medium text-gray-800">Amendment {idx + 1} — {log.amended_by_name}</p>
                <p className="text-gray-500 text-xs">
                  {log.amended_at ? new Date(log.amended_at).toLocaleString("en-IN") : "—"}
                </p>
                <p className="text-gray-700 mt-0.5">Reason: {log.reason}</p>
                <p className="text-gray-600 text-xs">
                  Grand total after: ₹{(log.grand_total_after || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Action Buttons Bar ── */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={downloadPO}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download PO (PDF)
        </button>

        {po.po_status === "ordered" && (
          <button
            onClick={async () => {
              if (!po.vendor_id) { alert("No vendor linked to this PO."); return; }
              try {
                const vendorSnap = await getDoc(doc(db, "vendors", po.vendor_id));
                if (!vendorSnap.exists()) { alert("Vendor not found."); return; }
                const vendor = vendorSnap.data();
                if (!vendor.email) { alert("No email on file for this vendor."); return; }
                const result = await sendVendorEmail(po, vendor.email, vendor.contact_person || "");
                if (result.success) {
                  alert(`✅ Email sent to ${vendor.email}`);
                } else {
                  alert(`Email not sent: ${result.reason}\n\nCheck EmailJS configuration in PurchaseOrderDetail.js`);
                }
              } catch (e) {
                alert("Failed to send email: " + e.message);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Resend Email to Vendor
          </button>
        )}
      </div>

      {/* ── TO-16: Approve PO banner — draft only ── */}
      {approveAllowed && (
        <div className="mb-4">
          {approveError && <p className="text-sm text-red-600 mb-2">{approveError}</p>}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-yellow-800">PO Pending Approval</p>
              <p className="text-xs text-yellow-700 mt-0.5">
                Review items and approve this PO. After approval, mark as Ordered to enable GRN.
              </p>
            </div>
            <button
              onClick={approvePO}
              disabled={approving}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition ml-4 whitespace-nowrap"
            >
              {approving ? "Approving..." : "✓ Approve PO"}
            </button>
          </div>
        </div>
      )}

      {/* ── TO-16: Mark as Ordered banner — approved only ── */}
      {orderAllowed && (
        <div className="mb-4">
          {markOrderedError && <p className="text-sm text-red-600 mb-2">{markOrderedError}</p>}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-800">PO Approved — Send to Vendor</p>
              <p className="text-xs text-blue-700 mt-0.5">
                Download PDF and share with vendor. Once sent, mark as Ordered to enable GRN.
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4 shrink-0">
              <button
                onClick={downloadPO}
                className="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition whitespace-nowrap"
              >
                ↓ Download PDF
              </button>
              <button
                onClick={markAsOrdered}
                disabled={markingOrdered}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition whitespace-nowrap"
              >
                {markingOrdered ? "Updating..." : "📦 Mark as Ordered"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GRN blocked banner — show when not yet ordered (and not in terminal state) ── */}
      {!receiveAllowed &&
        !approveAllowed &&
        !orderAllowed &&
        !TERMINAL_PO_STATUSES.includes(po.po_status) &&
        po.po_status !== "ordered" && (
        <div className="mb-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
          GRN not available — PO must be in <span className="font-medium">Ordered</span> status.
        </div>
      )}

      {/* ── GRN Button — only when ordered, and only for normal Stock
          Purchase. Direct Site Purchase routes through the two-stage
          Confirm/Finalize flow instead — never the regular GRN screen,
          which would wrongly credit central inventory. ── */}
      {receiveAllowed && po.purchase_mode !== "direct_site_purchase" && (
        <div className="flex justify-end">
          <button
            onClick={() => navigate(`/purchase-orders/receive/${poId}`)}
            className="px-5 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition"
          >
            Receive Material (GRN)
          </button>
        </div>
      )}

      {receiveAllowed && po.purchase_mode === "direct_site_purchase" && po.site_confirmation_status === "pending_site_confirmation" && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Waiting for the assigned Site Engineer to confirm received quantity at {po.site_name}.
        </div>
      )}

      {receiveAllowed && po.purchase_mode === "direct_site_purchase" && po.site_confirmation_status === "site_confirmed" &&
        ["sm", "owner", "manager"].includes(userRole) && (
        <div className="flex justify-end">
          <button
            onClick={() => navigate(`/purchase-orders/finalize-site/${poId}`)}
            className="px-5 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition"
          >
            ✓ Site Confirmed — Finalize Rate & Payment
          </button>
        </div>
      )}

      {/* ── Read-only badge + View GRN for completed/locked POs ── */}
      {(TERMINAL_PO_STATUSES.includes(po.po_status) || po.grn_locked) && (
        <div className="flex justify-end items-center gap-3">
          {/* ✅ View GRN button — GRN complete hone ke baad */}
          {po.grn_locked && (
            <button
              onClick={() => navigate(`/purchase-orders/${poId}/grn`)}
              className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition flex items-center gap-2"
            >
              ✅ View GRN Receipt
            </button>
          )}
          <span className="text-xs text-gray-400 bg-gray-100 px-3 py-2 rounded-lg">
            🔒 This PO is complete — read only
          </span>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          POST-APPROVE ACTION MODAL — TO-16 updated: reflects "approved" state
          ═══════════════════════════════════════════════════════════════════ */}
      {showPostApprove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  {/* TO-16: updated title — "approved" not "ordered" */}
                  <h3 className="text-base font-bold text-gray-900">PO Approved ✓</h3>
                  <p className="text-sm text-gray-500">
                    {po?.po_number} — Next step: Download PDF and mark as Ordered
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
                <p className="text-xs text-gray-500 mb-1">Vendor</p>
                <p className="text-sm font-semibold text-gray-800">{postApproveVendor?.name || "—"}</p>
                {postApproveVendor?.contact_person && (
                  <p className="text-xs text-gray-500">Attn: {postApproveVendor.contact_person}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  📧 {postApproveVendor?.email || <span className="text-red-500">No email on file</span>}
                </p>
              </div>

              {/* TO-16: next step hint */}
              <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl mb-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">Next Step</p>
                <p className="text-xs">Download the PO PDF and share with your vendor (WhatsApp / Email / Print). Then click <strong>Mark as Ordered</strong> to enable GRN.</p>
              </div>

              {emailStatus === "sent" && (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl mb-4 text-sm text-green-700">
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Email sent to {postApproveVendor?.email}
                </div>
              )}
              {emailStatus === "failed" && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl mb-4 text-sm text-red-700">
                  ✗ Email failed — check EmailJS configuration or send manually.
                </div>
              )}
              {emailStatus === "not_configured" && (
                <div className="px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl mb-4 text-sm text-yellow-700">
                  ⚠ EmailJS not configured. Download PDF and share manually.
                </div>
              )}

              <div className="space-y-2">
                <button
                  onClick={() => { downloadPO(); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PO (PDF)
                </button>

                {/* ✅ WhatsApp button */}
                {postApproveVendor?.phone ? (
                  <a
                    href={"https://wa.me/" +
                      postApproveVendor.phone
                        .replace(/[^0-9]/g, "")
                        .replace(/^0/, "91")
                        .replace(/^([6-9]\d{9})$/, "91$1") +
                      "?text=" + encodeURIComponent(
                      `Hi ${postApproveVendor.name || ""},\n\nPlease find Purchase Order *${po?.po_number}* from ${company?.company_name || "our company"}.\n\nItems:\n${
                        (po?.items || [])
                          .filter(i => !i._cancelled)
                          .map(i => `• ${i.item_name} — Qty: ${i.ordered_qty || 0} ${i.unit || ""}`)
                          .join("\n")
                      }\n\nKindly confirm receipt and delivery date.\n\nThank you.`
                      )}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#20b858] transition"
                  >
                    📱 Send via WhatsApp
                  </a>
                ) : (
                  <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-400 text-center">
                    📱 WhatsApp not available — no phone number on file for this vendor
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!postApproveVendor?.email) { setEmailStatus("failed"); return; }
                    setEmailSending(true);
                    setEmailStatus(null);
                    const result = await sendVendorEmail(approvedPOData || po, postApproveVendor.email, postApproveVendor.contact_person);
                    setEmailSending(false);
                    if (result.reason === "EmailJS not configured") {
                      setEmailStatus("not_configured");
                    } else if (result.success) {
                      setEmailStatus("sent");
                    } else {
                      setEmailStatus("failed");
                    }
                  }}
                  disabled={emailSending || !postApproveVendor?.email || emailStatus === "sent"}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {emailSending ? "Sending..." : emailStatus === "sent" ? "✓ Email Sent" : "Send Email to Vendor"}
                </button>

                <button
                  onClick={() => { setShowPostApprove(false); setEmailStatus(null); setPostApproveVendor(null); setApprovedPOData(null); }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-200 transition"
                >
                  Close — I'll mark as Ordered manually
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          AMEND PO MODAL — unchanged
          ═══════════════════════════════════════════════════════════════════ */}
      {showAmend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Amend Purchase Order</h2>
                <p className="text-sm text-gray-500">{po.po_number}</p>
              </div>
              <button onClick={() => setShowAmend(false)} className="text-gray-400 hover:text-gray-600 text-xl font-light">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {masterLoading && (
                <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Loading item catalogue...
                </div>
              )}
              {!masterLoading && itemsMaster.length > 0 && (
                <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                  ✓ {itemsMaster.length} items loaded
                </div>
              )}
              {!masterLoading && itemsMaster.length === 0 && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  ⚠ Item catalogue not loaded.
                </div>
              )}
              {amendError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {amendError}
                </div>
              )}

              <div className="space-y-3 mb-4">
                {amendItems.map((item) => {
                  const rowKey    = item._rowKey;
                  const rs        = searchState[rowKey] || { query: item.item_name || "", suggestions: [], open: false };
                  const qtyLocked = item._wasReceived;

                  return (
                    <div key={rowKey} className={`border rounded-lg p-4 ${item._cancelled ? "border-gray-200 bg-gray-50 opacity-70" : "border-gray-200 bg-white"}`}>
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[200px] relative">
                          <label className="text-xs text-gray-500 mb-1 block">Item Name</label>
                          <input
                            ref={(el) => { searchRefs.current[rowKey] = el; }}
                            type="text"
                            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                            value={rs.query || item.item_name || ""}
                            onChange={(e) => handleSearchChange(rowKey, e.target.value)}
                            disabled={item._cancelled || qtyLocked}
                            placeholder={masterLoading ? "Loading catalogue..." : "Type 2+ chars to search..."}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none disabled:bg-gray-100"
                          />
                          {rs.open && !item._cancelled && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-52 overflow-y-auto">
                              {rs.suggestions.map((suggestion) => (
                                <div
                                  key={suggestion.id}
                                  onClick={() => !suggestion._alreadyInPO && handleSelectSuggestion(rowKey, suggestion)}
                                  className={`px-4 py-3 ${suggestion._alreadyInPO ? "bg-gray-50 cursor-not-allowed" : "hover:bg-indigo-50 cursor-pointer"}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <p className={`text-sm font-medium ${suggestion._alreadyInPO ? "text-gray-400" : "text-gray-800"}`}>
                                      {suggestion.item_name}
                                    </p>
                                    {suggestion._alreadyInPO ? (
                                      <span className="text-xs text-orange-500 font-medium">Already in PO</span>
                                    ) : (
                                      <span className="text-xs text-gray-400">{suggestion.unit || ""}</span>
                                    )}
                                  </div>
                                  {suggestion.item_category && (
                                    <p className="text-xs text-gray-400 mt-0.5">{suggestion.item_category}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="w-24">
                          <label className="text-xs text-gray-500 mb-1 block">Unit</label>
                          <input type="text" value={item.unit || ""} onChange={(e) => handleRowChange(rowKey, "unit", e.target.value)} disabled={item._cancelled} placeholder="pcs" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none disabled:bg-gray-100" />
                        </div>
                        <div className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">
                            Ordered Qty {qtyLocked && <span className="text-orange-500">(locked)</span>}
                          </label>
                          <input type="number" min="0.01" step="0.01" value={item.ordered_qty || ""} onChange={(e) => handleRowChange(rowKey, "ordered_qty", e.target.value)} disabled={item._cancelled || qtyLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none disabled:bg-gray-100" />
                        </div>
                        <div className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">Rate (₹)</label>
                          <input type="number" min="0.01" step="0.01" value={item.rate || ""} onChange={(e) => handleRowChange(rowKey, "rate", e.target.value)} disabled={item._cancelled} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none disabled:bg-gray-100" />
                        </div>
                        <div className="w-28">
                          <label className="text-xs text-gray-500 mb-1 block">Amount</label>
                          <p className="px-3 py-2 text-sm font-medium text-gray-700">
                            ₹{((parseFloat(item.received_qty) || 0) * (parseFloat(item.rate) || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="flex items-end pb-1">
                          {item._cancelled ? (
                            <button onClick={() => handleRestoreItem(rowKey)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium py-2">Restore</button>
                          ) : (
                            <button onClick={() => handleCancelItem(rowKey)} className="text-xs text-red-500 hover:text-red-700 font-medium py-2">Cancel</button>
                          )}
                        </div>
                      </div>
                      {item._isCustom && !item._cancelled && (
                        <p className="text-xs text-purple-500 mt-1.5 flex items-center gap-1">
                          <span>⚡</span> Custom item — not in Item Master
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <button onClick={handleAddItem} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 mb-6">
                + Add Item
              </button>

              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-6 flex justify-between items-center">
                <p className="text-sm text-gray-600">Grand Total Preview (received × rate)</p>
                <p className="font-bold text-gray-900">
                  ₹{calcGrandTotal(amendItems).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amendment Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={reasonRef}
                  value={amendReason}
                  onChange={(e) => { setAmendReason(e.target.value); if (amendError?.includes("reason")) setAmendError(null); }}
                  rows={3}
                  placeholder="Enter reason for amendment (mandatory)..."
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none resize-none ${amendError?.includes("reason") ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              {!amendReason.trim() && (
                <p className="text-xs text-orange-600 mb-3 flex items-center gap-1">
                  ⚠ Amendment reason is required
                </p>
              )}
              <div className="flex items-center justify-between">
                <button onClick={() => setShowAmend(false)} disabled={amendSaving} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={handleSaveAmend} disabled={amendSaving} className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
                  {amendSaving ? "Saving..." : "Save Amendment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
